import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { inNativeApp } from "../tafo-app/native";
import { keepPageInPlace } from "../ui/scroll";
import { switchScreen } from "./switch-screen";
import { ApiError, createApiClient, type ApiClient, type ConnectionState } from "../api/client";
import { listUnconfirmed, replayAll } from "../api/pending";
import type {
  Authorization,
  Call,
  CodePurpose,
  DeviceStatus,
  FloorRoom,
  MenuResponse,
  Order,
  ParkedOrder,
  ParkedTicket,
  SoldOutProduct,
  Promotions,
  DeliverySettings,
  GeneralResponse,
  GeneralSettings,
  ClockDay,
  Street,
  TimeStatus,
  TermsStatus,
} from "../api/types";
import { CodeDialog, type CodeRequest } from "../features/codes/CodeDialog";
import { BeheerScreen } from "../features/beheer/BeheerScreen";
import { CouriersScreen } from "../features/couriers/CouriersScreen";
import { LiveMapScreen } from "../features/live-map/LiveMapScreen";
import { OrdersScreen } from "../features/orders/OrdersScreen";
import { useOrders } from "../features/orders/useOrders";
import { formatPhone } from "../lib/phone";
import { addressOf } from "../features/orders/order-view";
import { useCallerIdDevice } from "../features/calls/useCallerIdDevice";
import { bridgeWarnings, useBridgeHealth } from "../features/bridges/bridge-health";
import { ParkedScreen } from "../features/parked/ParkedScreen";
import { PrintProblemsDialog } from "../features/printing/PrintProblemsDialog";
import { usePrintStatus } from "../features/printing/usePrintStatus";
import { SaleScreen, type SaleHandle, type SaleStart } from "../features/sale/SaleScreen";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { DeviceIcon, GearIcon, MoonIcon, SunIcon } from "../ui/icons";
import { dialogOpen } from "../ui/Dialog";
import { ClockDialog } from "../features/time/ClockDialog";
import { t } from "../i18n";
import { ErrorBoundary } from "../ui/ErrorBoundary";
import { BarLogo } from "../ui/logo/BarLogo";
import { LicenceBarButton } from "../features/subscription/LicencePayment";
import { useToast } from "../ui/toasts";
import { SIZE_PERCENT, useSize } from "./size";
import { useTheme } from "./theme";
import { TillContext, type Till } from "./till-context";
import { TableQrRequests } from "../features/table-order/TableQrRequests";
import { LIVE_CLOSE, useLive } from "./useLive";
import { loadAllergenDictionary } from "../features/allergens/dictionary";
import { noticeDue, noticeSeen, TermsNotice } from "../features/terms/TermsNotice";
import { supportWindow } from "./support";
import { appDevice, appSession } from "../tafo-app/app-storage";
import { AppHead, ModeMenu, WhoButton, type ModeChoice } from "../tafo-app/AppScreens";
import { formatEuro } from "../lib/money";
import { dayName, shortDayName } from "../features/reports/reports";
import { useMissingZ } from "../features/reports/useMissingZ";

interface Props {
  api: ApiClient;
  status: DeviceStatus;
  deviceToken: string;
  handleFatal: (error: unknown) => boolean;
  restart: () => void;
}

/** The main views; Beheer is opened with a code and closes when another view is chosen. */
type Tab = "sale" | "orders" | "couriers" | "parked" | "map" | "beheer";
/** Ctrl+1 … Ctrl+4 (prototype): Verkoop, Bestellingen, Koeriers, Wachtlijst; Ctrl+5 the Live-kaart. */
const TAB_KEYS: Record<string, Tab> = { "1": "sale", "2": "orders", "3": "couriers", "4": "parked", "5": "map" };
const PENDING_CHECK_MS = 3_000;
/** Unconfirmed sales are sent again by themselves every 15 seconds (every fifth check). */
const REPLAY_EVERY_CHECKS = 5;

/**
 * The open till: top bar, the main screens, live updates and connection warnings. Nobody signs in;
 * a code is asked per action through `authorize` (the code window).
 */
export function TillShell({ api, status: started, deviceToken, handleFatal, restart }: Props) {
  // this device's name and kind can change in Beheer › Printers & toestellen
  const [device, setDevice] = useState(started.device);
  const status = useMemo(() => ({ ...started, device }), [started, device]);
  const toast = useToast();
  // the owner's phone (the Tafo app is paired here too): light or dark as the phone is set (Furkan, 18/10/2026)
  /** The owner's phone, in the Tafo app (not a till pc): the bar is red, as the head of the app. */
  const inApp = appDevice.read() !== null && status.device.type !== "pc";
  // in the app from the stores the page itself never scrolls: put it back if iOS moved it (scroll.ts)
  useEffect(() => (inApp && inNativeApp() ? keepPageInPlace() : undefined), [inApp]);
  const [theme, setTheme] = useTheme(inApp);
  const [size, setSize] = useSize();
  const otherSize = size === "s" ? "m" : "s";
  const [tab, setTab] = useState<Tab>("sale");
  /** Who opened Beheer; forgotten as soon as Beheer is left (as in the prototype). */
  const [beheerBy, setBeheerBy] = useState<Authorization | null>(null);
  const show = (next: Tab) => {
    setTab(next);
    if (next !== "beheer") setBeheerBy(null);
  };
  // on a narrow screen the tabs slide sideways: the chosen one is always in view (Furkan, 10/10/2026)
  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = tabsRef.current;
    const chosen = nav?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!nav || !chosen) return;
    const bar = nav.getBoundingClientRect();
    const one = chosen.getBoundingClientRect();
    if (one.left < bar.left) nav.scrollLeft -= bar.left - one.left + 8;
    else if (one.right > bar.right) nav.scrollLeft += one.right - bar.right + 8;
  }, [tab]);
  /** What the sale screen starts with: an order reopened for editing, or a parked ticket (Verder werken). */
  const [start, setStart] = useState<SaleStart | null>(null);
  const sale = useRef<SaleHandle>(null);
  const [codeRequest, setCodeRequest] = useState<
    (CodeRequest & { resolve: (authorization: Authorization | null) => void }) | null
  >(null);
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [floor, setFloor] = useState<FloorRoom[] | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("online");
  const [pendingCount, setPendingCount] = useState(() => listUnconfirmed().length);
  // Afdrukken (blok I): the Tafo app for Windows prints; every till sees when it does not
  const printing = usePrintStatus(api, started.device.role === "till");
  /** Failed jobs set aside here with "Negeren". */
  const [printDismissed, setPrintDismissed] = useState<ReadonlySet<string>>(new Set());
  const [printProblems, setPrintProblems] = useState(false);
  const printFailed = (printing.status?.failed ?? []).filter((job) => !printDismissed.has(job.id));
  const printersOffline =
    printing.status !== null && printing.status.printers > 0 && printing.status.agents.length === 0;
  // the Takeaway and Uber Eats bridges on the till PC: silent, hanging or logged out (Furkan, 23/10/2026)
  const bridgeLines = bridgeWarnings(useBridgeHealth(api, started.device.role === "till"));

  const report = useCallback(
    (error: unknown) => {
      if (handleFatal(error)) return;
      toast(error instanceof ApiError ? error.message : t.errors.unexpected, "bad");
    },
    [handleFatal, toast],
  );

  const orders = useOrders(api, report);
  /** "Betalen bij afhalen" from the Tafo app: takeaways in the kitchen, paid at the till (Wachtlijst). */
  const awaiting = useMemo(
    () => orders.orders.filter((order) => order.channel === "takeaway" && order.status === "open"),
    [orders.orders],
  );

  const loadMenu = useCallback(() => {
    api.request<MenuResponse>("GET", "/device/menu", { retry: true }).then(setMenu).catch(report);
  }, [api, report]);
  useEffect(loadMenu, [loadMenu]);

  const loadFloor = useCallback(() => {
    api
      .request<{ rooms: FloorRoom[] }>("GET", "/device/floor", { retry: true })
      .then(({ rooms }) => {
        setFloor(rooms);
      })
      .catch((error: unknown) => {
        // without the floor plan the till still sells (takeaway, delivery); the error is shown
        setFloor((current) => current ?? []);
        report(error);
      });
  }, [api, report]);
  useEffect(loadFloor, [loadFloor]);

  const [soldOutList, setSoldOutList] = useState<SoldOutProduct[]>([]);
  const loadSoldOut = useCallback(() => {
    api
      .request<{ products: SoldOutProduct[] }>("GET", "/device/sold-out", { retry: true })
      .then(({ products }) => {
        setSoldOutList(products);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadSoldOut, [loadSoldOut]);
  const soldOut = useMemo(() => new Set(soldOutList.map((product) => product.productId)), [soldOutList]);

  const [staffRevision, setStaffRevision] = useState(0);
  /** Koeriers reloads after every change of an order, a courier or a settlement on any device. */
  const [couriersRevision, setCouriersRevision] = useState(0);
  const [printingRevision, setPrintingRevision] = useState(0);
  const [reportsRevision, setReportsRevision] = useState(0);
  /** After every Z: the orange bar of the days without a Z asks again. */
  const [zRevision, setZRevision] = useState(0);
  const missingZ = useMissingZ(api, zRevision);
  const [reportSettingsRevision, setReportSettingsRevision] = useState(0);
  const [subscriptionRevision, setSubscriptionRevision] = useState(0);
  const [time, setTime] = useState<TimeStatus | null>(null);
  const loadTime = useCallback(() => {
    api.request<TimeStatus>("GET", "/device/time", { retry: true }).then(setTime).catch(report);
  }, [api, report]);
  useEffect(loadTime, [loadTime]);
  const [clocking, setClocking] = useState<{ authorization: Authorization; day: ClockDay } | null>(null);
  const [delivery, setDelivery] = useState<DeliverySettings | null>(null);
  const loadDelivery = useCallback(() => {
    api
      .request<{ delivery: DeliverySettings }>("GET", "/device/delivery", { retry: true })
      .then((answer) => {
        setDelivery(answer.delivery);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadDelivery, [loadDelivery]);
  const [streets, setStreets] = useState<Street[]>([]);
  const loadStreets = useCallback(() => {
    api
      .request<{ streets: Street[] }>("GET", "/device/streets", { retry: true })
      .then((answer) => {
        setStreets(answer.streets);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadStreets, [loadStreets]);
  const [customersRevision, setCustomersRevision] = useState(0);

  // de Wachtlijst: the parked tickets, the same on every device
  const [parked, setParked] = useState<ParkedOrder[]>([]);
  const loadParked = useCallback(() => {
    api
      .request<{ parked: ParkedOrder[] }>("GET", "/device/parked", { retry: true })
      .then((answer) => {
        setParked(answer.parked);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadParked, [loadParked]);
  const [parkedBusy, setParkedBusy] = useState(false);
  /** "Verder werken" while something is on the ticket: park that first? */
  const [resumeAsk, setResumeAsk] = useState<ParkedOrder | null>(null);

  // Nummerherkenning: the call ringing now, on every till; the Bellerslijst follows every change
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [callsRevision, setCallsRevision] = useState(0);
  /** QR-bestellen aan tafel: the guests' orders that wait for a till to accept them are asked again. */
  const [tableQrRevision, setTableQrRevision] = useState(0);
  const callArrived = useCallback(
    (id: string) => {
      api
        .request<{ calls: Call[] }>("GET", "/device/calls", { retry: true })
        .then(({ calls }) => {
          const call = calls.find((item) => item.id === id);
          if (!call || call.taken) return;
          setIncomingCall(call);
          toast(t.calls.toast(formatPhone(call.phone), call.customer?.name ?? null));
        })
        .catch(report);
    },
    [api, report, toast],
  );
  // the caller-ID device on this till pc, when one was chosen in Beheer › Printers & toestellen
  useCallerIdDevice(api, status.device.role === "till");

  const [general, setGeneral] = useState<GeneralSettings | null>(null);
  const [receiptQr, setReceiptQr] = useState(false);
  const loadGeneral = useCallback(() => {
    api
      .request<GeneralResponse>("GET", "/device/general", { retry: true })
      .then((answer) => {
        setGeneral(answer.general);
        setReceiptQr(answer.receiptQr);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadGeneral, [loadGeneral]);

  const [promotions, setPromotions] = useState<Promotions | null>(null);
  const loadPromotions = useCallback(() => {
    api
      .request<{ promotions: Promotions }>("GET", "/device/promotions", { retry: true })
      .then((answer) => {
        setPromotions(answer.promotions);
      })
      .catch(report);
  }, [api, report]);
  useEffect(loadPromotions, [loadPromotions]);

  useEffect(() => api.onConnectionChange(setConnection), [api]);

  /** Sends sales that were not confirmed (crash, lost connection) again, with their original keys. */
  const replayPending = useCallback(
    () =>
      replayAll(api, {
        onSaved: (label, order) => {
          toast(`${label}: ${t.sale.saved(order.code)}`);
        },
        onRefused: report,
      }).then(setPendingCount),
    [api, report, toast],
  );

  useEffect(() => {
    void replayPending();
    let checks = 0;
    const timer = setInterval(() => {
      const count = listUnconfirmed().length;
      setPendingCount(count);
      if (count > 0 && ++checks % REPLAY_EVERY_CHECKS === 0) void replayPending();
    }, PENDING_CHECK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [replayPending]);

  useEffect(() => {
    loadAllergenDictionary(api);
  }, [api]);

  // a new version of the terms announced by Tafo: a notice once per start-up (never in a support window)
  const [termsNotice, setTermsNotice] = useState<TermsStatus["upcoming"]>(() =>
    supportWindow.read() === null && noticeDue(started.terms.upcoming) ? started.terms.upcoming : null,
  );
  const checkTermsNotice = () => {
    api
      .request<DeviceStatus>("GET", "/device", { retry: true })
      .then((fresh) => {
        if (supportWindow.read() === null && noticeDue(fresh.terms.upcoming)) setTermsNotice(fresh.terms.upcoming);
      })
      .catch(() => undefined);
  };

  useLive(deviceToken, {
    onMessage: (message) => {
      orders.apply(message);
      if (message.type === "menu.updated" || message.type === "resync") loadMenu();
      if (message.type === "allergens.updated" || message.type === "resync") loadAllergenDictionary(api);
      if (message.type === "terms.published") checkTermsNotice();
      if (message.type === "floor.updated" || message.type === "resync") loadFloor();
      if (message.type === "soldout.updated" || message.type === "resync") loadSoldOut();
      // clocking in or out changes the status in Personeel too ("● ingeklokt")
      if (message.type === "staff.updated" || message.type === "time.updated" || message.type === "resync") {
        setStaffRevision((count) => count + 1);
      }
      if (message.type === "time.updated" || message.type === "resync") loadTime();
      if (message.type === "delivery.updated" || message.type === "resync") loadDelivery();
      if (message.type === "streets.updated" || message.type === "resync") loadStreets();
      if (message.type === "parked.updated" || message.type === "resync") loadParked();
      if (message.type === "customers.updated" || message.type === "resync") {
        setCustomersRevision((count) => count + 1);
      }
      if (message.type === "call.incoming") callArrived(message.id);
      if (message.type === "table_qr.requested" || message.type === "table_qr.decided" || message.type === "resync") {
        setTableQrRevision((count) => count + 1);
      }
      // a courier's problem at the door (Tafo app): every till hears it at once
      if (message.type === "courier.problem") {
        const problem = message.order.problems.at(-1);
        if (problem) {
          toast(
            t.couriers.problemToast(problem.by ?? "", problem.reason, message.order.code, addressOf(message.order)),
            "bad",
          );
        }
      }
      // the QR on a receipt was paid: Bancontact said so and Tafo booked it; every till hears it
      if (message.type === "receipt_qr.paid") {
        const paid = message.order.payments.findLast((payment) => payment.method === "bank_qr");
        toast(t.checkout.receiptQr.paid(message.order.code, formatEuro(paid?.amountCents ?? message.order.totalCents)));
      }
      // not printed: said at once on every till, and kept in the bar until printed again or set aside
      if (message.type === "print.failed") {
        void printing.reload().then((fresh) => {
          const job = fresh?.failed.find((item) => item.id === message.id);
          if (job) toast(t.print.failedToast(job.title, job.printer), "bad");
        });
      }
      if (
        message.type === "printing.updated" ||
        message.type === "resync" ||
        // what waits while no Tafo app prints
        (message.type === "print.queued" && printersOffline)
      ) {
        void printing.reload();
      }
      // taken over on another till: it stops ringing here
      if (message.type === "call.updated") {
        setIncomingCall((call) => (call?.id === message.id ? null : call));
      }
      if (message.type === "call.incoming" || message.type === "call.updated" || message.type === "resync") {
        setCallsRevision((count) => count + 1);
      }
      if (message.type === "general.updated" || message.type === "resync") loadGeneral();
      // the orders of a closed Z can no longer change: the list shows it
      if (message.type === "z.closed") orders.reload();
      if (message.type === "z.closed" || message.type === "resync") setZRevision((count) => count + 1);
      if (
        message.type === "order.created" ||
        message.type === "order.updated" ||
        message.type === "order.deleted" ||
        message.type === "z.closed" ||
        message.type === "resync"
      ) {
        setReportsRevision((count) => count + 1);
      }
      if (
        message.type === "order.created" ||
        message.type === "order.updated" ||
        message.type === "order.deleted" ||
        message.type === "couriers.updated" ||
        message.type === "courier.problem" ||
        message.type === "staff.updated" ||
        message.type === "z.closed" ||
        message.type === "resync"
      ) {
        setCouriersRevision((count) => count + 1);
      }
      if (message.type === "reports.updated" || message.type === "resync") {
        setReportSettingsRevision((count) => count + 1);
      }
      if (
        message.type === "subscription.updated" ||
        message.type === "terms.accepted" ||
        message.type === "terms.published" ||
        message.type === "resync"
      ) {
        setSubscriptionRevision((count) => count + 1);
      }
      if (
        message.type === "printing.updated" ||
        message.type === "device.updated" ||
        message.type === "device.revoked" ||
        message.type === "resync"
      ) {
        setPrintingRevision((count) => count + 1);
      }
      if (message.type === "device.updated" && message.id === device.id) {
        api
          .request<DeviceStatus>("GET", "/device", { retry: true })
          .then((fresh) => {
            setDevice(fresh.device);
          })
          .catch(report);
      }
      // a menu change can change the actions too (a removed product or category)
      if (message.type === "promotions.updated" || message.type === "menu.updated" || message.type === "resync") {
        loadPromotions();
      }
    },
    onRefused: (code) => {
      if (code === LIVE_CLOSE.notPaired) handleFatal(new ApiError(401, "device_not_paired", ""));
      else restart();
    },
  });

  const authorize = useCallback(
    (permission: CodePurpose, title: string) =>
      new Promise<Authorization | null>((resolve) => {
        setCodeRequest({ permission, title, resolve });
      }),
    [],
  );
  // Beheer after 30 minutes: the session of the code that opened it has ended. The code is asked again
  // and the work goes on where it was; before, Beheer had to be closed and opened (Furkan, 05/10/2026).
  const beheerSession = useRef<string | null>(null);
  useEffect(() => {
    beheerSession.current = beheerBy?.token ?? null;
  }, [beheerBy]);
  useEffect(() => {
    api.onCodeExpired(async (ended) => {
      if (beheerSession.current !== ended) return null;
      const again = await authorize("beheer", t.codes.beheerAgain);
      if (again === null) return null;
      setBeheerBy(again);
      return again.token;
    });
    return () => {
      api.onCodeExpired(null);
    };
  }, [api, authorize]);
  const openTables = useMemo(
    () =>
      new Map(
        orders.orders
          .filter((order) => order.channel === "table" && order.status === "open" && order.table !== null)
          .map((order) => [order.table!, order]),
      ),
    [orders.orders],
  );
  const till = useMemo<Till | null>(
    () =>
      menu && floor
        ? {
            api,
            status,
            menu,
            floor,
            floorChanged: setFloor,
            openTables,
            orderSaved: orders.upsert,
            soldOut,
            soldOutChanged: setSoldOutList,
            promotions,
            promotionsChanged: setPromotions,
            staffRevision,
            printingRevision,
            reportsRevision,
            reportSettingsRevision,
            subscriptionRevision,
            time,
            timeChanged: setTime,
            delivery,
            deliveryChanged: setDelivery,
            streets,
            customersRevision,
            incomingCall,
            callHandled: () => {
              setIncomingCall(null);
            },
            callsRevision,
            general,
            generalChanged: setGeneral,
            receiptQr,
            printStatus: printing.status,
            deviceChanged: setDevice,
            menuChanged: setMenu,
            reloadMenu: loadMenu,
            paymentMethods: status.paymentMethods,
            authorize,
            report,
            toast,
          }
        : null,
    [
      api,
      status,
      menu,
      floor,
      openTables,
      orders.upsert,
      soldOut,
      promotions,
      staffRevision,
      printingRevision,
      reportsRevision,
      reportSettingsRevision,
      subscriptionRevision,
      time,
      delivery,
      streets,
      customersRevision,
      incomingCall,
      callsRevision,
      general,
      receiptQr,
      printing.status,
      loadMenu,
      authorize,
      report,
      toast,
    ],
  );

  /** The sale screen starts over with this ticket (a new key: a fresh cart). */
  const saleKey =
    start?.kind === "edit" ? `edit-${start.order.id}` : start?.kind === "resume" ? `parked-${start.id}` : "new";

  /** "Bewerken" (Bestellingen, Beheer): the order on the ticket, as in the prototype. */
  const editOrder = (order: Order, authorization: Authorization | null) => {
    setStart({ kind: "edit", order, authorization });
    show("sale");
    toast(t.editing.opened(order.code));
  };

  /** "Verder werken": the parked ticket leaves the Wachtlijst and comes on the ticket of this till. */
  const takeBack = async (entry: ParkedOrder) => {
    setParkedBusy(true);
    try {
      const { ticket } = await api.request<{ ticket: ParkedTicket }>("POST", `/device/parked/${entry.id}/resume`);
      setParked((list) => list.filter((item) => item.id !== entry.id));
      setStart({ kind: "resume", id: entry.id, ticket });
      show("sale");
    } catch (error) {
      report(error);
      loadParked();
    } finally {
      setParkedBusy(false);
    }
  };

  const resume = async (entry: ParkedOrder) => {
    if (sale.current?.editing() === true) {
      toast(t.editing.finishFirst, "bad");
      return;
    }
    // something on the ticket: park it first, so nothing is lost (the prototype overwrote it)
    if (sale.current?.busy() === true) {
      setResumeAsk(entry);
      return;
    }
    await takeBack(entry);
  };

  /** "Weggooien": the parked ticket leaves the list. */
  const drop = async (entry: ParkedOrder) => {
    setParkedBusy(true);
    try {
      await api.request("DELETE", `/device/parked/${entry.id}`);
      setParked((list) => list.filter((item) => item.id !== entry.id));
      toast(t.parked.dropped);
    } catch (error) {
      report(error);
      loadParked();
    } finally {
      setParkedBusy(false);
    }
  };

  /**
   * The owner's phone came from the Tafo app: back to it as Ober or Koerier. The app is set to that role first
   * (as its own menu does); when that is refused, the app opens as it was and says why itself.
   */
  const toApp = appDevice.read() !== null && status.device.role === "till" && status.device.type !== "pc";
  const appStaff = toApp ? (appSession.read()?.staff ?? null) : null;
  const appChoices: ModeChoice[] = [
    ...(["waiter", "courier"] as const).filter(
      (role) => appStaff === null || appStaff.can.includes(role === "waiter" ? "orders.create" : "courier.app"),
    ),
    "till",
  ];
  const [leaving, setLeaving] = useState(false);
  const appClient = () =>
    createApiClient({ deviceToken: () => appDevice.read(), staffToken: () => appSession.read()?.token ?? null });
  /** "Afmelden" under the letter, as in the app: the app opens at the names. */
  const signOutOfApp = () => {
    void appClient()
      .request("DELETE", "/device/sessions/current")
      .catch(() => undefined);
    appSession.clear();
    switchScreen("/app");
  };
  const toTafoApp = async (role: "waiter" | "courier") => {
    setLeaving(true);
    const app = appClient();
    try {
      await app.request("POST", "/device/app-mode", { body: { role } });
    } catch {
      // a waiter or courier only, or signed out: the app shows where it is
    }
    switchScreen("/app");
  };

  /** Ctrl+1 … Ctrl+4 (prototype): Verkoop, Bestellingen, Koeriers, Wachtlijst; Ctrl+5 Live-kaart; not over a dialog. */
  const onKey = useEffectEvent((event: KeyboardEvent) => {
    if (!event.ctrlKey || event.altKey || event.metaKey || dialogOpen() || !till) return;
    const target = TAB_KEYS[event.key];
    if (target === undefined) return;
    event.preventDefault();
    show(target);
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onKey(event);
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  return (
    <div className="app">
      {/* in the Tafo app the bar is red as the head of Ober and Koerier: the strip of the clock above it, red in
          the app, is one with it (Furkan, 18/10/2026: a red piece above a dark bar looked unfinished) */}
      {/* the owner's phone: the red head of the Tafo app itself, "Kassa ⌄" at the same place as "Ober ⌄" there, to
          go back as Ober or Koerier (Furkan, 18/10 and 19/10/2026) */}
      {toApp && (
        <AppHead
          left={
            <ModeMenu
              current="till"
              choices={appChoices}
              disabled={leaving}
              onPick={(choice) => {
                if (choice !== "till") void toTafoApp(choice);
              }}
            />
          }
          right={appStaff !== null && <WhoButton name={appStaff.name} onSignOut={signOutOfApp} />}
        />
      )}
      {/* under the red head: the bar of the till in its own colours, the tabs in the grey part (Furkan, 19/10/2026) */}
      <header className={inApp && !toApp ? "top in-app" : "top"}>
        {!toApp && <BarLogo white={inApp}>{t.app.till}</BarLogo>}
        {/* the payment of the licence, again after "Later betalen" (Furkan, 27/10/2026) */}
        <LicenceBarButton licence={status.licence} />
        <nav className="tabs" role="tablist" ref={tabsRef}>
          <button type="button" role="tab" aria-selected={tab === "sale"} onClick={() => show("sale")}>
            {t.app.sale}
          </button>
          <button type="button" role="tab" aria-selected={tab === "orders"} onClick={() => show("orders")}>
            {t.app.orders}
          </button>
          <button type="button" role="tab" aria-selected={tab === "couriers"} onClick={() => show("couriers")}>
            {t.app.couriers}
          </button>
          {/* prototype: Wachtlijst with the number of parked tickets, on every device */}
          <button type="button" role="tab" aria-selected={tab === "parked"} onClick={() => show("parked")}>
            {t.app.parked}
            {parked.length + awaiting.length > 0 && <span className="badge">{parked.length + awaiting.length}</span>}
          </button>
          {/* where the couriers on a ride are (Furkan, 09/10/2026) */}
          <button type="button" role="tab" aria-selected={tab === "map"} onClick={() => show("map")}>
            {t.app.liveMap}
          </button>
        </nav>
        <span className="spacer" />
        <div className="who">
          <span>{status.shop.name}</span>
          <span className="chip-label device" title={t.app.deviceHint}>
            <DeviceIcon type={status.device.type} />
            {status.device.name}
          </span>
          {/* S or M: how large the till is drawn on this device (ADR 0021; Furkan, 04/10/2026). The
              letter is the size the till has now; a tap gives the other one. */}
          {/* not on the owner's phone: a phone has its own size, and on an iPhone the switch left the page zoomed in
              (Furkan, 18/10/2026) */}
          {!inApp && (
            <button
              type="button"
              className="btn gear symbol"
              aria-label={t.app.size.label(t.app.size.name[size])}
              title={t.app.size.hint(
                t.app.size.name[size],
                SIZE_PERCENT[size],
                t.app.size.name[otherSize],
                SIZE_PERCENT[otherSize],
              )}
              onClick={() => {
                setSize(otherSize);
              }}
            >
              <span className="letter" aria-hidden="true">
                {t.app.size.name[size]}
              </span>
            </button>
          )}
          {/* Wit or Zwart: one small symbol, as on a phone (Furkan, 03/10/2026). It shows what a tap
              gives: the moon by day, the sun by night. */}
          <button
            type="button"
            className="btn gear symbol"
            role="switch"
            aria-checked={theme === "dark"}
            aria-label={t.app.dark}
            title={theme === "dark" ? t.app.toLight : t.app.toDark}
            onClick={() => {
              setTheme(theme === "dark" ? "light" : "dark");
            }}
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          </button>
          {/* clocking in and out: only on the till pc, and only when the owner has it on */}
          {time?.on === true && status.device.role === "till" && status.device.type === "pc" && (
            <button
              type="button"
              className="btn gear"
              disabled={!till}
              onClick={() => {
                if (!till) return;
                void till
                  .authorize("clock", t.time.code)
                  .then(async (authorization) => {
                    if (!authorization) return;
                    const { day } = await api.request<{ day: ClockDay }>("GET", "/device/time/me", {
                      staffToken: authorization.token,
                    });
                    setClocking({ authorization, day });
                  })
                  .catch(report);
              }}
            >
              {t.time.button}
              {time.clockedIn > 0 && (
                <span className="badge" title={t.time.clockedInCount(time.clockedIn)}>
                  {time.clockedIn}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            className="btn gear symbol"
            aria-label={t.beheer.title}
            title={t.beheer.title}
            // until the menu and the floor plan are loaded a click would be lost
            disabled={!till}
            onClick={() => {
              if (tab === "beheer" || !till) return;
              void till.authorize("beheer", t.codes.beheer).then((authorization) => {
                if (!authorization) return;
                setBeheerBy(authorization);
                setTab("beheer");
              });
            }}
          >
            <GearIcon />
          </button>
          <Clock />
        </div>
      </header>

      {connection === "trouble" && (
        <div className="banner warn" role="status">
          {t.errors.connection}
        </div>
      )}
      {/* a day without a Z, until its Z is made (Furkan, 11/10/2026) */}
      {missingZ.length > 0 && (
        <div className="banner warn zbanner" role="status">
          {missingZ.length === 1
            ? t.reports.banner.one(dayName(missingZ[0]!.day))
            : t.reports.banner.many(
                missingZ.length,
                shortDayName(missingZ[0]!.day),
                shortDayName(missingZ[missingZ.length - 1]!.day),
              )}
        </div>
      )}
      {pendingCount > 0 && (
        <div className="banner bad" role="alert">
          <span>{t.errors.unconfirmed(pendingCount)}</span>
          <span className="spacer" />
          <button type="button" className="btn small" onClick={() => void replayPending()}>
            {t.errors.retryNow}
          </button>
        </div>
      )}
      {printersOffline && (
        <div className="banner warn" role="status">
          <span>{t.print.offline(printing.status?.waiting ?? 0)}</span>
          <span className="spacer" />
          <button type="button" className="btn small" onClick={() => setPrintProblems(true)}>
            {t.print.show}
          </button>
        </div>
      )}
      {bridgeLines.map((line) => (
        <div key={line} className="banner warn" role="status">
          {line}
        </div>
      ))}
      {printFailed.length > 0 && (
        <div className="banner bad" role="alert">
          <span>{t.print.failed(printFailed.length)}</span>
          <span className="spacer" />
          <button type="button" className="btn small" onClick={() => setPrintProblems(true)}>
            {t.print.show}
          </button>
        </div>
      )}

      <div className="view">
        {till ? (
          <TillContext value={till}>
            {/* the sale screen stays loaded behind the other views: its ticket is still there when coming back */}
            <ErrorBoundary key={saleKey}>
              <SaleScreen
                key={saleKey}
                ref={sale}
                start={start}
                active={tab === "sale"}
                onEditDone={(order) => {
                  setStart(null);
                  if (order) orders.upsert(order);
                }}
              />
            </ErrorBoundary>
            {tab !== "sale" && (
              <ErrorBoundary key={tab}>
                {tab === "beheer" && beheerBy ? (
                  <BeheerScreen
                    authorization={beheerBy}
                    orders={orders}
                    onEdit={editOrder}
                    onClose={() => {
                      show("sale");
                    }}
                  />
                ) : tab === "couriers" ? (
                  <CouriersScreen revision={couriersRevision} />
                ) : tab === "map" ? (
                  <LiveMapScreen revision={couriersRevision} />
                ) : tab === "parked" ? (
                  <ParkedScreen
                    parked={parked}
                    awaiting={awaiting}
                    busy={parkedBusy}
                    onResume={(entry) => void resume(entry)}
                    onDrop={(entry) => void drop(entry)}
                  />
                ) : (
                  <OrdersScreen state={orders} onEdit={editOrder} />
                )}
              </ErrorBoundary>
            )}
          </TillContext>
        ) : (
          <div className="panel" aria-busy="true" />
        )}
      </div>

      {resumeAsk !== null && (
        <ConfirmDialog
          message={t.parked.busy}
          tone="primary"
          confirm={t.parked.parkAndResume}
          onConfirm={() => {
            const entry = resumeAsk;
            setResumeAsk(null);
            void sale.current?.park().then((parkedNow) => {
              if (parkedNow) void takeBack(entry);
            });
          }}
          onClose={() => {
            setResumeAsk(null);
          }}
        />
      )}
      {clocking !== null && (
        <ClockDialog
          api={api}
          authorization={clocking.authorization}
          day={clocking.day}
          toast={toast}
          onClose={() => {
            setClocking(null);
          }}
        />
      )}
      {printProblems && printing.status !== null && (
        <PrintProblemsDialog
          api={api}
          status={printing.status}
          failed={printFailed}
          onDismiss={(ids) => {
            setPrintDismissed((current) => new Set([...current, ...ids]));
          }}
          onChanged={() => void printing.reload()}
          toast={toast}
          report={report}
          onClose={() => {
            setPrintProblems(false);
          }}
        />
      )}
      {codeRequest !== null && (
        <CodeDialog
          api={api}
          permission={codeRequest.permission}
          title={codeRequest.title}
          onAuthorized={(authorization) => {
            codeRequest.resolve(authorization);
            setCodeRequest(null);
          }}
          onClose={() => {
            codeRequest.resolve(null);
            setCodeRequest(null);
          }}
        />
      )}
      <TableQrRequests api={api} revision={tableQrRevision} toast={toast} report={report} />
      {termsNotice !== null && (
        <TermsNotice
          api={api}
          upcoming={termsNotice}
          onClose={() => {
            noticeSeen(termsNotice.version);
            setTermsNotice(null);
          }}
        />
      )}
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 10_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return <span className="clock">{now.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}</span>;
}
